/**
 * authFetch - Azure AD Bearer token injector
 *
 * Injects Authorization: Bearer <id_token> on every backend API call.
 * If called before the token is set (race condition on cold load), it waits
 * until setAuthToken() is called from App.tsx, then proceeds automatically.
 * Calls that already carry an Authorization header (MSA/Flux) are left untouched.
 */

let _token: string | null = null;
let _resolve: ((t: string) => void) | null = null;
const _tokenReady: Promise<string> = new Promise(res => { _resolve = res; });

/**
 * Decode JWT token payload (without verification)
 */
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if token is expired or about to expire (within 5 minutes)
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    console.warn('⚠️ Cannot decode token or missing exp claim');
    return true;
  }

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

  const isExpired = currentTime >= (expirationTime - bufferTime);
  
  if (isExpired) {
    const timeLeft = Math.floor((expirationTime - currentTime) / 1000);
    console.warn(`⚠️ Token expired or expiring soon (${timeLeft}s left)`);
  }

  return isExpired;
};

/**
 * Get token expiration info for logging/debugging
 */
export const getTokenInfo = (token: string): { exp: number; iat: number; timeLeft: string } | null => {
  const payload = decodeJWT(token);
  if (!payload) return null;

  const exp = payload.exp * 1000;
  const iat = payload.iat * 1000;
  const now = Date.now();
  const timeLeftMs = exp - now;
  const timeLeftMin = Math.floor(timeLeftMs / 60000);
  const timeLeftSec = Math.floor((timeLeftMs % 60000) / 1000);

  return {
    exp,
    iat,
    timeLeft: timeLeftMs > 0 
      ? `${timeLeftMin}m ${timeLeftSec}s` 
      : 'EXPIRED'
  };
};

/**
 * Get the current authenticated user's identity from the stored JWT token.
 * Checks claims in priority order: preferred_username → upn → email → oid → sub
 */
export const getCurrentUser = (): string => {
  if (!_token) return 'unknown';
  const payload = decodeJWT(_token);
  if (!payload) return 'unknown';
  return (
    payload.preferred_username ||
    payload.upn ||
    payload.email ||
    payload.oid ||
    payload.sub ||
    'unknown'
  );
};

export const setAuthToken = (token: string): void => {
  _token = token;
  
  // Log token expiration info
  const tokenInfo = getTokenInfo(token);
  if (tokenInfo) {
    const issuedAt = new Date(tokenInfo.iat).toLocaleString();
    const expiresAt = new Date(tokenInfo.exp).toLocaleString();
    const tokenPreview = token.substring(0, 50) + '...';
    console.log('🔑 Token set:');
    console.log(`   Token preview: ${tokenPreview}`);
    console.log(`   Issued at: ${issuedAt}`);
    console.log(`   Expires at: ${expiresAt}`);
    console.log(`   Time remaining: ${tokenInfo.timeLeft}`);
  }
  
  if (_resolve) {
    _resolve(token);
    _resolve = null;
  }
};

export const authFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  // If token not yet set, wait for it (resolves as soon as App.tsx calls setAuthToken)
  const token = _token ?? await _tokenReady;

  // Check if token is expired or about to expire
  if (isTokenExpired(token)) {
    console.error('❌ Token is expired or about to expire! API call may fail.');
    console.log('💡 Tip: Refresh the page or re-login to get a new token');
  } else {
    const tokenInfo = getTokenInfo(token);
    if (tokenInfo) {
      console.log(`🌐 API call to: ${url.substring(0, 80)}...`);
      console.log(`   Using token with ${tokenInfo.timeLeft} remaining`);
    }
  }

  const headers = new Headers(init.headers as HeadersInit | undefined);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(url, { ...init, headers });
};
